/* ========================================================================
   INFORMATIONS GÉNÉRALES SUR LE SITE
   Propriété de © 2019/2024 Shopiweb.fr
   Pour plus d'informations, visitez : https://www.shopiweb.fr
   ======================================================================== */

/* =====================
   Recharger l'ensemble de la collection de produits avec un contenu actualisé
   ===================== */
let collectionRevision = 0;
let collectionController = null;
let collectionBusy = false;

const collectionSectionURL = (href) => {
  const url = new URL(href, window.location.href);
  const collection = document.querySelector(".collection");
  const sectionId = collection?.dataset.sectionId || collection?.closest('[id^="shopify-section-"]')?.id.replace("shopify-section-", "");
  if (!sectionId) throw new Error("Collection section missing");
  url.searchParams.delete("sections");
  url.searchParams.set("section_id", sectionId);
  return url;
};

const reloadCollection = async () => {
  cancelCollectionPriceUpdates();
  const revision = ++collectionRevision;
  collectionController?.abort();
  const controller = new AbortController();
  collectionController = controller;
  collectionBusy = true;
  const href = window.location.href;
  const productListing = document.querySelector(".collection .product-listing");
  if (productListing) productListing.style.opacity = "0.2";
  window.scrollTo({ top: 0, behavior: "smooth" });
  try {
    const response = await fetch(collectionSectionURL(href).href, { signal: controller.signal });
    if (!response.ok) throw new Error("Collection HTTP error");
    const html = await response.text();
    if (revision !== collectionRevision) return;
    const updatedDocument = new DOMParser().parseFromString(html, "text/html");
    const updatedCollection = updatedDocument.querySelector(".collection");
    if (!updatedCollection || updatedCollection.id !== document.querySelector(".collection")?.id) throw new Error("Collection fragment missing");
    const restoreCollectionFocus = window.captureThemeFocus(".collection", '#sort-by, h1, .product-listing');
    const restoreFilterFocus = window.captureThemeFocus("#offcanvas-filters", '.btn-close');
    disposeCollectionUI();
    document.querySelector(".collection")?.replaceWith(updatedCollection);
    updateUIComponents(updatedDocument);
    document.querySelectorAll('.collection [data-bs-toggle="popover"]').forEach((el) => {
      bootstrap.Popover.getOrCreateInstance(el);
    });
    window.dispatchEvent(new CustomEvent("onCollectionShopiwebUpdate"));
    restoreCollectionFocus();
    restoreFilterFocus();
  } catch (error) {
    if (revision === collectionRevision && error.name !== "AbortError") window.location.assign(href);
  } finally {
    if (revision === collectionRevision) {
      collectionBusy = false;
      collectionController = null;
      const listing = document.querySelector(".collection .product-listing");
      if (listing) listing.style.opacity = "1";
    }
  }
};

// Updates filter components and other interactive elements within the offcanvas filters
const updateUIComponents = (updatedDocument) => {
  // Update collapsible filter internals
  document
    .querySelectorAll("#offcanvas-filters .collapse-inner")
    .forEach((collapse) => {
      const collapseId = collapse.closest(".collapse").getAttribute("id");
      const newCollapseContent = updatedDocument.querySelector(
        `#offcanvas-filters #${collapseId} .collapse-inner`
      );
      if (newCollapseContent) {
        collapse.replaceWith(newCollapseContent);
      }
    });

  // Replace specific filter UI components such as footer, clear all button, and sort by selector
  const uiSelectors = [
    "#offcanvas-filters .offcanvas-footer",
    "#offcanvas-filters .btn-filters-clear-all",
    '#offcanvas-filters [name="sort_by"]',
  ];
  uiSelectors.forEach((selector) => {
    const newElement = updatedDocument.querySelector(selector);
    if (newElement) document.querySelector(selector)?.replaceWith(newElement);
  });
};

/* =====================
   Utilitaire pour mettre à jour l'URL avec de nouveaux paramètres de requête
   ===================== */
const updateUrlWithQueryParams = (form) => {
  const params = new URLSearchParams(new FormData(form));
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, "", newUrl);
};

/* =====================
   Mise à jour de la collection en fonction des changements de filtre
   ===================== */
window.onChangeCollectionFilter = async (inputElement) => {
  const form = inputElement.closest("form");
  updateUrlWithQueryParams(form);
  await reloadCollection();
};

/* =====================
   Gestion des changements de filtres de prix de la collection
   ===================== */
const collectionPriceTimers = new Map();
const collectionPriceSliders = new Map();
let collectionPriceListening = false;

const cancelCollectionPriceUpdates = () => {
  collectionPriceTimers.forEach((timer) => clearTimeout(timer));
  collectionPriceTimers.clear();
};

const onCollectionPriceInput = (event) => {
  const input = event.target;
  if (!input.matches?.(".filter-amounts input")) return;
  const form = input.closest("form");
  if (!form) return;
  clearTimeout(collectionPriceTimers.get(form));
  const timer = setTimeout(() => {
    collectionPriceTimers.delete(form);
    if (!input.isConnected || !form.isConnected) return;
    updateUrlWithQueryParams(form);
    reloadCollection();
  }, 750);
  collectionPriceTimers.set(form, timer);
};

const setupPriceFilterListeners = () => {
  if (collectionPriceListening) return;
  document.addEventListener("input", onCollectionPriceInput);
  collectionPriceListening = true;
};

/* =====================
   Développe tous les filtres cachés en cliquant sur le bouton
   ===================== */
window.onClickFiltersViewMore = (button) => {
  button
    .closest(".collapse")
    .querySelectorAll(".form-check")
    .forEach((check) => {
      check.removeAttribute("hidden");
    });
  button.remove();
};

/* =====================
   Efface tous les filtres et recharge la collection
   ===================== */
window.onClickClearAllFilters = async (button) => {
  const form = button.closest("form");
  const sortValue = form.querySelector('[name="sort_by"]').value;
  const params = new URLSearchParams();
  params.set("sort_by", sortValue);
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}?${params.toString()}`
  );
  await reloadCollection();
};

/* =====================
   Initialise les curseurs du filtre de prix
   ===================== */
const initializePriceFilterSliders = () => {
  collectionPriceSliders.forEach((api, slider) => {
    if (!slider.isConnected) {
      api.destroy();
      collectionPriceSliders.delete(slider);
    }
  });
  if (!window.noUiSlider) return;
  document.querySelectorAll(".amount-selection-slider").forEach((slider) => {
    if (collectionPriceSliders.has(slider)) return;
    const form = slider.closest("form");
    const minValueField = form?.querySelector('[name="filter.v.price.gte"]');
    const maxValueField = form?.querySelector('[name="filter.v.price.lte"]');
    if (!minValueField || !maxValueField) return;
    if (slider.noUiSlider) slider.noUiSlider.destroy();
    window.noUiSlider.create(slider, {
      start: [Number(slider.dataset.minValue), Number(slider.dataset.maxValue)],
      connect: true,
      range: {
        min: Number(slider.dataset.rangeMin),
        max: Number(slider.dataset.rangeMax),
      },
    });
    const api = slider.noUiSlider;
    collectionPriceSliders.set(slider, api);
    api.on("update", (values) => {
      minValueField.value = values[0];
      maxValueField.value = values[1];
    });
    api.on("change", () => {
      minValueField.dispatchEvent(new Event("input", { bubbles: true }));
    });
  });
};

/* =====================
   Met à jour les paramètres de l'URL
   ===================== */
const updateQueryParameters = (key, value) => {
  const params = new URLSearchParams(window.location.search);
  params.delete("page");
  params.set(key, value);
  return `${window.location.pathname}?${params.toString()}`;
};

/* =====================
   Gère les changements dans le tri de la collection
   ===================== */
window.onChangeCollectionSortBy = (sortByValue) => {
  const updatedUrl = updateQueryParameters("sort_by", sortByValue);
  window.history.replaceState({}, "", updatedUrl);
  reloadCollection();
};

/* =====================
   Fonctions permettant de charger d'autres produits dans une collection à la suite d'une action de l'utilisateur.
   ===================== */
const showLoadingIndicator = (button) => {
  button.style.width = `${button.offsetWidth + 2}px`;
  button.style.height = `${button.offsetHeight + 2}px`;
  button.innerHTML = `
        <div class="spinner-border mx-auto spinner-border-sm" style="width: 1.2rem; height: 1.2rem" role="status">
            <span class="visually-hidden">En attente...</span>
        </div>
    `;
};

const updateCollectionContent = async (button) => {
  const currentList = document.querySelector(".collection .product-listing");
  const href = button.dataset.nextUrl;
  if (collectionBusy || !button.isConnected || button.disabled || !currentList || !href) return;
  const url = new URL(href, window.location.href);
  const pageParam = currentList.dataset.pageParam || "page";
  const nextPage = Number(url.searchParams.get(pageParam));
  if (nextPage !== Number(currentList.dataset.collectionPage) + 1) return;
  const previewId = new URL(window.location.href).searchParams.get("preview_theme_id");
  if (previewId) url.searchParams.set("preview_theme_id", previewId);
  const revision = ++collectionRevision;
  const controller = new AbortController();
  collectionController = controller;
  collectionBusy = true;
  stopCollectionAutoPagination();
  const restorePagingFocus = window.captureThemeFocus(".collection", '.button-loading-more, .product-listing');
  const originalHTML = button.innerHTML;
  const originalStyle = button.getAttribute("style");
  button.disabled = true;
  showLoadingIndicator(button);
  let updated = false;
  try {
    const response = await fetch(collectionSectionURL(url.href).href, { signal: controller.signal });
    if (!response.ok) throw new Error("Pagination HTTP error");
    const html = await response.text();
    if (revision !== collectionRevision || !currentList.isConnected) return;
    const newDocument = new DOMParser().parseFromString(html, "text/html");
    const collection = newDocument.querySelector(".collection");
    const list = collection?.querySelector(".product-listing");
    const pagination = collection?.querySelector("#paging-collection");
    if (collection?.id !== currentList.closest(".collection").id || !list || !pagination || Number(list.dataset.collectionPage) !== nextPage) {
      throw new Error("Pagination fragments invalid");
    }
    const productKey = (item) => {
      const link = item.querySelector("a.product-link");
      return link ? new URL(link.getAttribute("href"), window.location.href).pathname : null;
    };
    const products = new Set(Array.from(currentList.children, productKey).filter(Boolean));
    for (const item of Array.from(list.children)) {
      const key = productKey(item);
      if (key && products.has(key)) continue;
      if (key) products.add(key);
      currentList.append(item);
    }
    currentList.dataset.collectionPage = String(nextPage);
    document.querySelector("#paging-collection")?.replaceWith(pagination);
    document.querySelectorAll('.collection [data-bs-toggle="popover"]').forEach((el) => {
      bootstrap.Popover.getOrCreateInstance(el);
    });
    updated = true;
    restorePagingFocus();
  } catch (error) {
    if (revision === collectionRevision && error.name !== "AbortError") {
      console.warn("Collection pagination: loading can be retried.");
    }
  } finally {
    if (button.isConnected) {
      button.disabled = false;
      button.innerHTML = originalHTML;
      if (originalStyle === null) button.removeAttribute("style");
      else button.setAttribute("style", originalStyle);
    }
    if (revision === collectionRevision) {
      collectionBusy = false;
      collectionController = null;
      if (updated) window.dispatchEvent(new CustomEvent("onCollectionShopiwebUpdate"));
    }
  }
};

window.onClickCollectionLoadMore = async (button, event) => {
  event.preventDefault();
  await updateCollectionContent(button);
};

/* =====================
   Pagination infinie automatique
   ===================== */
let collectionPagination = null;
let collectionSticky = null;

const stopCollectionAutoPagination = () => {
  collectionPagination?.observer.disconnect();
  collectionPagination = null;
};

const initializeAutoPagination = () => {
  const pagination = document.querySelector("#paging-collection");
  if (collectionPagination?.element === pagination) return;
  stopCollectionAutoPagination();
  if (!pagination?.querySelector(".button-loading-more") || !window.IntersectionObserver) return;
  const state = { element: pagination, observer: null };
  state.observer = new window.IntersectionObserver((entries) => {
    if (collectionPagination !== state || !pagination.isConnected || collectionBusy) return;
    if (!entries.some((entry) => entry.target === pagination && entry.isIntersecting)) return;
    const button = pagination.querySelector(".button-loading-more");
    if (button && !button.disabled) button.click();
  }, { threshold: 0, rootMargin: "0px 0px -100px 0px" });
  collectionPagination = state;
  state.observer.observe(pagination);
};

const stopCollectionStickyBar = () => {
  const state = collectionSticky;
  if (!state) return;
  document.removeEventListener("scroll", state.schedule);
  window.removeEventListener("resize", state.schedule);
  if (state.frame !== null) cancelAnimationFrame(state.frame);
  state.bar.style.top = state.originalTop;
  state.bar.classList.remove("pa-controls-docked");
  collectionSticky = null;
};

const toggleStickyUtilitiesBar = () => {
  const bar = document.querySelector("#collection-controls");
  if (collectionSticky?.bar === bar) return;
  stopCollectionStickyBar();
  if (!bar) return;
  const state = { bar, originalTop: bar.style.top, frame: null, schedule: null };
  const update = () => {
    state.frame = null;
    if (collectionSticky !== state || !bar.isConnected) return;
    let top = 0;
    document.querySelectorAll('[id*="__navbar"].sticky-top, [id*="__announcement-bar"].sticky-top').forEach((header) => {
      const rect = header.getBoundingClientRect();
      if (rect.top <= top + 1 && rect.bottom > top) top = rect.bottom;
    });
    bar.style.top = `${Math.max(0, top)}px`;
    bar.classList.toggle("pa-controls-docked", top > 0 && bar.getBoundingClientRect().top <= top + 1);
  };
  state.schedule = () => {
    if (state.frame === null) state.frame = requestAnimationFrame(update);
  };
  collectionSticky = state;
  document.addEventListener("scroll", state.schedule, { passive: true });
  window.addEventListener("resize", state.schedule);
  update();
};

const collectionDesktop = window.matchMedia('(min-width: 992px)');
const collectionColumns = (viewport) => {
  const value = document.documentElement.getAttribute('data-pa-columns-' + viewport);
  const options = viewport === 'desktop' ? ['2', '4', '6'] : ['1', '2', '4'];
  return options.includes(value) ? value : viewport === 'mobile' ? '2' : '4';
};

const initializeCollectionLayout = () => {
  const collection = document.querySelector('.collection');
  if (!collection) return;
  const columns = collectionColumns(collectionDesktop.matches ? 'desktop' : 'mobile');
  collection.querySelectorAll('[data-collection-columns]').forEach((button) => {
    const value = collectionDesktop.matches ? button.dataset.collectionColumnsDesktop : button.dataset.collectionColumns;
    button.setAttribute('aria-pressed', String(value === columns));
    button.setAttribute('aria-label', collectionDesktop.matches ? button.dataset.labelDesktop : button.dataset.labelMobile);
  });
  const controls = collection.querySelector('.pa-collection-layout');
  if (controls) controls.hidden = false;
  const mobile = Number(collectionColumns('mobile'));
  const desktop = Math.max(4, Number(collectionColumns('desktop')));
  const sizes = `(min-width: 992px) calc((100vw - ${(desktop - 1) * 4}px) / ${desktop}), calc((100vw - ${(mobile - 1) * 4}px) / ${mobile})`;
  const updateImage = (image) => {
    if (image.hasAttribute('srcset') && image.sizes !== sizes) image.sizes = sizes;
  };
  collection.querySelectorAll('.collection-products-grid .product-item-img').forEach(updateImage);
  collection.querySelectorAll('.collection-products-grid template').forEach((template) => {
    template.content.querySelectorAll('img').forEach(updateImage);
  });
};

collectionDesktop.addEventListener('change', initializeCollectionLayout);

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-collection-columns]');
  if (!button?.closest('.collection')) return;
  const viewport = collectionDesktop.matches ? 'desktop' : 'mobile';
  const columns = collectionDesktop.matches ? button.dataset.collectionColumnsDesktop : button.dataset.collectionColumns;
  const options = collectionDesktop.matches ? ['2', '4', '6'] : ['1', '2', '4'];
  if (!options.includes(columns)) return;
  document.querySelector('#pa-quick-add[open] [data-pa-quick-close]')?.click();
  document.documentElement.setAttribute('data-pa-columns-' + viewport, columns);
  try { localStorage.setItem('pa-collection-columns-' + viewport, columns); } catch {}
  initializeCollectionLayout();
});

const disposeCollectionUI = () => {
  stopCollectionAutoPagination();
  stopCollectionStickyBar();
  cancelCollectionPriceUpdates();
  if (collectionPriceListening) {
    document.removeEventListener("input", onCollectionPriceInput);
    collectionPriceListening = false;
  }
  collectionPriceSliders.forEach((api) => api.destroy());
  collectionPriceSliders.clear();
};

const initializeCollectionUI = () => {
  if (!document.querySelector(".collection")) {
    disposeCollectionUI();
    return;
  }
  initializeCollectionLayout();
  setupPriceFilterListeners();
  initializePriceFilterSliders();
  initializeAutoPagination();
  toggleStickyUtilitiesBar();
};

const singlePageCollection = document.querySelector('.collection[data-single-page="true"]');
const firstPageURL = new URL(window.location.href);
if (singlePageCollection && Number(firstPageURL.searchParams.get("page")) > 1) {
  firstPageURL.searchParams.delete("page");
  window.location.replace(firstPageURL.href);
} else {
  initializeCollectionUI();
}
window.addEventListener("onCollectionShopiwebUpdate", initializeCollectionUI);

document.addEventListener("shopify:section:unload", (event) => {
  if (!event.target.matches?.(".collection") && !event.target.querySelector?.(".collection")) return;
  collectionRevision += 1;
  collectionController?.abort();
  collectionController = null;
  collectionBusy = false;
  disposeCollectionUI();
});

document.addEventListener("shopify:section:load", (event) => {
  if (event.target.matches?.(".collection") || event.target.querySelector?.(".collection")) initializeCollectionUI();
});

/* =====================
   Bannière de collection
   ===================== */
const initializeCollectionBanners = () => {
  const productListing = document.querySelector(".collection .product-listing");

  if (!productListing) return;

  // Process each banner and insert it before the specified product item
  document.querySelectorAll(".collection-card").forEach((b) => {
    const injectionIndex = Number(b.dataset.inject) - 1; 
    const targetItem = productListing.children[injectionIndex]; 

    if (targetItem) {
      targetItem.insertAdjacentElement("beforebegin", b);
    }
  });
};
initializeCollectionBanners();

// Handle the loading of Shopify sections which might include collection banners
document.addEventListener("shopify:section:load", (event) => {
  if (event.target.querySelector(".collection-card")) {
    // Re-initialize banners upon dynamic content loading to ensure correct placement
    initializeCollectionBanners();
  }
});


